import React, { useMemo, useState } from 'react';
import { Select, Button, Input } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { EXCHANGE_RATES_FOR_YEAR, SAVE_EXCHANGE_RATE } from '@graphql/exchangeRate.gql';
import styles from './ExchangeRate.module.css';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_OPTIONS = MONTHS.map((label, i) => ({
  value: i + 1,
  label,
}));

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => ({
  value: y,
  label: String(y),
}));

const buildMonthYear = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;

const ExchangeRate = () => {
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [inputMonth, setInputMonth] = useState<number>(1);
  const [inputRate, setInputRate] = useState('');
  const [inputError, setInputError] = useState('');

  const { data, refetch } = useQuery(EXCHANGE_RATES_FOR_YEAR, {
    variables: { year: selectedYear },
    fetchPolicy: 'cache-and-network',
  });

  const [saveExchangeRate, { loading: saving }] = useMutation(SAVE_EXCHANGE_RATE);

  const rateByMonth = useMemo(() => {
    const map: Record<number, number> = {};
    (data?.exchangeRatesForYear ?? []).forEach((r: { monthYear: string; rate: number }) => {
      const month = Number(r.monthYear.slice(5, 7));
      map[month] = r.rate;
    });
    return map;
  }, [data]);

  const getRateForMonth = (month: number): number | null => rateByMonth[month] ?? null;

  const handleSave = async () => {
    const parsed = parseFloat(inputRate);
    if (!inputRate.trim() || isNaN(parsed) || parsed <= 0) {
      setInputError('Enter a valid positive number');
      return;
    }

    try {
      await saveExchangeRate({
        variables: { monthYear: buildMonthYear(selectedYear, inputMonth), rate: parsed },
      });
      await refetch();
      setInputRate('');
      setInputError('');
    } catch (err: any) {
      setInputError(err?.message || 'Failed to save exchange rate');
    }
  };

  return (
    <div className={styles.container} data-testid="exchange-rate">
      <div className={styles.header}>
        <span className={styles.title}>Exchange Rates</span>
        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          options={YEAR_OPTIONS.map((o) => ({ ...o, 'data-testid': 'exchange-rate-year-option', 'data-value': o.label }))}
          style={{ width: 100 }}
          data-testid="exchange-rate-year-select"
        />
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {MONTHS.map((m) => (
                <th key={m} className={styles.th}>{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {MONTHS.map((_, i) => {
                const val = getRateForMonth(i + 1);
                return (
                  <td key={i} className={styles.td} data-testid="exchange-rate-value" data-month={MONTHS[i].toLowerCase()}>
                    {val != null ? val.toFixed(4) : '—'}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className={styles.inputRow}>
        <Select
          value={inputMonth}
          onChange={setInputMonth}
          options={MONTH_OPTIONS.map((o) => ({ ...o, 'data-testid': 'exchange-rate-month-option', 'data-value': o.label.toLowerCase() }))}
          style={{ width: 90 }}
          data-testid="exchange-rate-month-select"
        />
        <Input
          value={inputRate}
          onChange={(e) => { setInputRate(e.target.value); setInputError(''); }}
          placeholder="Rate"
          style={{ width: 120 }}
          status={inputError ? 'error' : ''}
          onPressEnter={handleSave}
          data-testid="exchange-rate-input"
        />
        <Button type="primary" onClick={handleSave} loading={saving} data-testid="exchange-rate-save-button">Save</Button>
        {inputError && <span className={styles.error} data-testid="exchange-rate-error">{inputError}</span>}
      </div>
    </div>
  );
};

export default ExchangeRate;
